import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, ScrollText } from "lucide-react";

function fmtInr(n) {
  return `₹ ${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VoucherList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [vtype, setVtype] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (vtype !== "all") params.voucher_type = vtype;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await api.get("/voucher/vouchers", { params });
    setItems(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vtype, dateFrom, dateTo]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Vouchers</h1>
        </div>
        <Link to="/voucher/vouchers/new">
          <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="new-voucher-button">
            <Plus className="w-4 h-4 mr-1.5" /> New Voucher
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
          <Input
            placeholder="Voucher no., customer, vehicle…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9"
            data-testid="voucher-search-input"
          />
        </div>
        <Select value={vtype} onValueChange={setVtype}>
          <SelectTrigger className="w-44" data-testid="voucher-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="travelling">Travelling Voucher</SelectItem>
            <SelectItem value="delivery">Delivery Challan</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="voucher-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="voucher-date-to" />
        <Button variant="outline" onClick={load} data-testid="voucher-apply-filters">Apply</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm flex flex-col items-center gap-2">
            <ScrollText className="w-8 h-8 text-zinc-300" />
            No vouchers found.
          </div>
        ) : (
          <div className="divide-y divide-border" data-testid="voucher-list">
            {/* Table header - desktop only */}
            <div className="hidden md:grid grid-cols-[160px_100px_1fr_120px_140px_140px] items-center gap-3 p-3 bg-zinc-50 text-xs text-zinc-500 overline font-semibold">
              <div>Voucher No.</div>
              <div>Type</div>
              <div>Customer</div>
              <div>Date</div>
              <div className="text-right">Net Weight</div>
              <div className="text-right">Grand Total</div>
            </div>
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-border">
              {items.map((v) => (
                <Link
                  key={v.id}
                  to={`/voucher/vouchers/${v.id}`}
                  className="block p-3 hover:bg-zinc-50"
                  data-testid={`voucher-row-${v.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm">{v.voucher_no}</div>
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${v.voucher_type === "travelling" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                      {v.voucher_type === "travelling" ? "Travelling" : "Delivery"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{v.to_customer_name || "—"}</div>
                      <div className="text-xs text-zinc-500 truncate">{v.vehicle_number || "—"} {v.salesman_name ? ` · ${v.salesman_name}` : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-zinc-500">{v.voucher_date?.slice(0, 10)}</div>
                      <div className="text-sm num font-semibold">{fmtInr(v.grand_total)}</div>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>Net Wt: <span className="num font-medium text-zinc-900">{(v.total_net_weight || 0).toFixed(3)} g</span></span>
                  </div>
                </Link>
              ))}
            </div>
            {/* Desktop table view */}
            <div className="hidden md:block">
              {items.map((v) => (
                <Link
                  key={v.id}
                  to={`/voucher/vouchers/${v.id}`}
                  className="grid grid-cols-[160px_100px_1fr_120px_140px_140px] items-center gap-3 p-3 hover:bg-zinc-50"
                  data-testid={`voucher-row-${v.id}`}
                >
                  <div className="font-mono text-sm">{v.voucher_no}</div>
                  <div>
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${v.voucher_type === "travelling" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                      {v.voucher_type === "travelling" ? "Travelling" : "Delivery"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{v.to_customer_name || "—"}</div>
                    <div className="text-xs text-zinc-500 truncate">{v.vehicle_number || "—"} {v.salesman_name ? ` · ${v.salesman_name}` : ""}</div>
                  </div>
                  <div className="text-sm">{v.voucher_date?.slice(0, 10)}</div>
                  <div className="text-sm num text-right">{(v.total_net_weight || 0).toFixed(3)} g</div>
                  <div className="text-sm num font-semibold text-right">{fmtInr(v.grand_total)}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
